// Authentication utilities for JK Legacy
// Handles username/password, face auth simulation, and TOTP-style rotating pins

// Generate 6-digit TOTP-style code that changes every minute
export function generateTOTPCode(seed = 'jk-legacy-secret') {
  const now = Math.floor(Date.now() / 60000); // Changes every minute
  const today = new Date().toDateString(); // Daily seed component
  const combined = `${seed}-${today}-${now}`;
  
  // Simple hash function
  let hash = 0;
  for (let i = 0; i < combined.length; i++) {
    const char = combined.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  // Generate 6-digit code
  const code = Math.abs(hash % 1000000).toString().padStart(6, '0');
  return code;
}

// Get time until next code rotation (in seconds)
export function getSecondsUntilNextCode() {
  const now = Date.now();
  const nextMinute = Math.ceil(now / 60000) * 60000;
  return Math.floor((nextMinute - now) / 1000);
}

// Validate username and password
export function validateCredentials(username, password) {
  // Only allow predefined users - no custom users allowed
  const predefinedUser = PREDEFINED_USERS[username.toLowerCase()];
  if (predefinedUser) {
    const inputHash = hashPassword(password);
    if (inputHash === predefinedUser.passwordHash) {
      return { isValid: true, isFirstTime: false };
    }
  }
  
  // Reject all other login attempts
  return { isValid: false, isFirstTime: false };
}

// Save credentials (first time setup)
export function saveCredentials(username, password) {
  const passwordHash = hashPassword(password);
  localStorage.setItem('jk_auth_credentials', JSON.stringify({ username, passwordHash }));
}

// Authentication utilities for JK Legacy

// Predefined user credentials (hashed) with email addresses
const PREDEFINED_USERS = {
  'rujaiananth': {
    username: 'rujaiananth',
    passwordHash: hashPassword('RJ@jvrpt7384'),
    email: 'rujaiananthj@gmail.com'
  },
  'prajitkannan': {
    username: 'prajitkannan',
    passwordHash: hashPassword('PK@jvrpt7384'),
    email: 'prajitkannanj@gmail.com'
  },
  'thamojkarthic': {
    username: 'thamojkarthic',
    passwordHash: hashPassword('TK@jvrpt7384'),
    email: 'thamojkarthicj@gmail.com'
  },
  'jeyakumar': {
    username: 'jeyakumar',
    passwordHash: hashPassword('JK@jvrpt7384'),
    email: 'jeyakumarpaulraj73@gmail.com'
  },
  'viji': {
    username: 'viji',
    passwordHash: hashPassword('VJ@jvrpt7384'),
    email: 'vijijeyakumar84@gmail.com'
  }
};

// Simple hash function for passwords (in production, use bcrypt or similar)
function hashPassword(password) {
  let hash = 0;
  for (let i = 0; i < password.length; i++) {
    const char = password.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash.toString(36);
}

// Initialize face authentication (request camera access)
export async function initFaceAuth() {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ 
      video: { facingMode: 'user' } 
    });
    return { supported: true, stream };
  } catch (err) {
    console.warn('Camera not available:', err.message);
    return { supported: false, stream: null };
  }
}

// Family member face references
const FAMILY_FACES = {
  'rujaiananth': ['rujaiananth.JPG', 'rujaiananth2.JPG'],
  'prajitkannan': ['prajit.JPG'],
  'thamojkarthic': ['thamoj.HEIC'],
  'jeyakumar': ['jeyakumar.PNG'],
  'viji': ['viji.JPG']
};

// Verify face against stored family member photos
export async function verifyFace(videoElement, username) {
  return new Promise((resolve) => {
    setTimeout(async () => {
      try {
        // Get the reference photos for this user
        const userFaces = FAMILY_FACES[username.toLowerCase()];
        if (!userFaces) {
          resolve({ verified: false, message: 'No face reference found for user' });
          return;
        }

        // Capture current frame from video
        const canvas = document.createElement('canvas');
        canvas.width = videoElement.videoWidth;
        canvas.height = videoElement.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(videoElement, 0, 0);
        
        // TO IMPLEMENT REAL FACE RECOGNITION:
        // 
        // 1. Install face-api.js:
        //    npm install face-api.js
        //
        // 2. Load models on app initialization:
        //    import * as faceapi from 'face-api.js';
        //    await faceapi.nets.ssdMobilenetv1.loadFromUri('/models');
        //    await faceapi.nets.faceLandmark68Net.loadFromUri('/models');
        //    await faceapi.nets.faceRecognitionNet.loadFromUri('/models');
        //
        // 3. Extract descriptors from reference photos (do this once, store in localStorage):
        //    const referenceDescriptors = {};
        //    for (const [user, photos] of Object.entries(FAMILY_FACES)) {
        //      const descriptors = [];
        //      for (const photo of photos) {
        //        const img = await faceapi.fetchImage(`/Family Pics/${photo}`);
        //        const detection = await faceapi.detectSingleFace(img)
        //          .withFaceLandmarks()
        //          .withFaceDescriptor();
        //        if (detection) descriptors.push(detection.descriptor);
        //      }
        //      referenceDescriptors[user] = descriptors;
        //    }
        //
        // 4. Compare live video frame with reference:
        //    const detection = await faceapi.detectSingleFace(canvas)
        //      .withFaceLandmarks()
        //      .withFaceDescriptor();
        //    
        //    if (detection) {
        //      const userDescriptors = referenceDescriptors[username];
        //      const distances = userDescriptors.map(desc => 
        //        faceapi.euclideanDistance(detection.descriptor, desc)
        //      );
        //      const minDistance = Math.min(...distances);
        //      const threshold = 0.6; // Adjust based on testing
        //      
        //      if (minDistance < threshold) {
        //        // Face matches!
        //        resolve({ verified: true, message: 'Face verified successfully' });
        //      } else {
        //        resolve({ verified: false, message: 'Face does not match' });
        //      }
        //    } else {
        //      resolve({ verified: false, message: 'No face detected' });
        //    }
        
        // Current: Simulated face recognition (80% success rate)
        // Replace with real implementation above
        const faceDetected = Math.random() > 0.2;
        
        if (faceDetected) {
          sessionStorage.setItem('jk_face_auth_time', Date.now().toString());
          sessionStorage.setItem('jk_face_auth_user', username);
          resolve({ verified: true, message: 'Face verified successfully' });
        } else {
          resolve({ verified: false, message: 'Face not recognized' });
        }
      } catch (err) {
        console.error('Face verification error:', err);
        resolve({ verified: false, message: 'Verification failed' });
      }
    }, 2000);
  });
}

// Check if face auth is still valid (5 minute window)
export function isFaceAuthValid() {
  const timestamp = sessionStorage.getItem('jk_face_auth_time');
  if (!timestamp) return false;
  
  const elapsed = Date.now() - parseInt(timestamp);
  return elapsed < 5 * 60 * 1000; // 5 minutes
}

// Get user email
export function getUserEmail(username) {
  const user = PREDEFINED_USERS[username.toLowerCase()];
  return user ? user.email : null;
}

// Send TOTP code to user's email
export async function sendTOTPEmail(username) {
  const user = PREDEFINED_USERS[username.toLowerCase()];
  if (!user) {
    return { success: false, message: 'User not found' };
  }

  const code = generateTOTPCode(`jk-${username}-2024`);
  const email = user.email;

  try {
    // Send email via local backend server
    const response = await fetch('http://localhost:3001/api/send-auth-code', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: email,
        username: username,
        code: code
      })
    });

    const result = await response.json();

    if (result.success) {
      console.log('✓ Email sent successfully via backend');

      // Store that email was sent
      sessionStorage.setItem('jk_totp_email_sent', JSON.stringify({
        username,
        email,
        timestamp: Date.now()
      }));

      return { 
        success: true, 
        message: `Code sent to ${email.replace(/(.{3}).*(@.*)/, '$1***$2')}`,
        email: email.replace(/(.{3}).*(@.*)/, '$1***$2')
      };
    } else {
      throw new Error(result.message || 'Failed to send email');
    }

  } catch (error) {
    console.error('Failed to send email:', error);
    
    // Fallback: log to console for testing
    console.log(`📧 FALLBACK - TOTP Code for ${username} (${email}): ${code}`);
    
    return { 
      success: false, 
      message: 'Failed to send email. Check if backend server is running on port 3001.' 
    };
  }
}

// Generate daily seed for TOTP
export function getDailySeed() {
  const date = new Date();
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}

// Create session token
export function createSession() {
  const sessionId = Math.random().toString(36).substring(2) + Date.now().toString(36);
  const expiresAt = Date.now() + (24 * 60 * 60 * 1000); // 24 hours
  
  const session = { sessionId, expiresAt, createdAt: Date.now() };
  sessionStorage.setItem('jk_session', JSON.stringify(session));
  return session;
}

// Validate session
export function isSessionValid() {
  const stored = sessionStorage.getItem('jk_session');
  if (!stored) return false;
  
  try {
    const { expiresAt } = JSON.parse(stored);
    return Date.now() < expiresAt;
  } catch (e) {
    return false;
  }
}

// Logout
export function logout() {
  sessionStorage.removeItem('jk_session');
  sessionStorage.removeItem('jk_face_auth_time');
}
